import type { NoteImageSlot, NoteStyle, NoteTemplate, CopywritingStyle } from '@/types'
import type { ReceiptType } from './receipts'

const IDS = {
  // 餐饮探店UGC 风格
  styleFoodUcg: '6f6f5c2a-3c5e-4f6c-9b53-1f3df0c5a001',
  tplFoodUcg01: '6f6f5c2a-3c5e-4f6c-9b53-1f3df0c5a002',
  slotFoodStorefront: '6f6f5c2a-3c5e-4f6c-9b53-1f3df0c5a101',
  slotFoodCrowd: '6f6f5c2a-3c5e-4f6c-9b53-1f3df0c5a102',
  slotFoodTable: '6f6f5c2a-3c5e-4f6c-9b53-1f3df0c5a103',
  slotFoodDish: '6f6f5c2a-3c5e-4f6c-9b53-1f3df0c5a104',
  // 餐饮探店UGC 文案风格
  cpyFoodBestie: '6f6f5c2a-3c5e-4f6c-9b53-1f3df0c5a201',
  cpyFoodDiary: '6f6f5c2a-3c5e-4f6c-9b53-1f3df0c5a202',
  cpyFoodReview: '6f6f5c2a-3c5e-4f6c-9b53-1f3df0c5a203',
  cpyFoodExcited: '6f6f5c2a-3c5e-4f6c-9b53-1f3df0c5a204',

  // 旅游攻略类 风格
  styleTravelGuide: 'd2b77f8f-1f8c-4f8c-9224-4c5e8f2b0001',
  tplTravelGuide01: 'd2b77f8f-1f8c-4f8c-9224-4c5e8f2b0002',
  slotTravelCover: 'd2b77f8f-1f8c-4f8c-9224-4c5e8f2b0101',
  slotTravelItinerary: 'd2b77f8f-1f8c-4f8c-9224-4c5e8f2b0102',
  slotTravelSpots: 'd2b77f8f-1f8c-4f8c-9224-4c5e8f2b0103',
  slotTravelTraffic: 'd2b77f8f-1f8c-4f8c-9224-4c5e8f2b0104',
  slotTravelFood: 'd2b77f8f-1f8c-4f8c-9224-4c5e8f2b0105',
  slotTravelRoute: 'd2b77f8f-1f8c-4f8c-9224-4c5e8f2b0106',
  slotTravelTips: 'd2b77f8f-1f8c-4f8c-9224-4c5e8f2b0107',
  // 旅游攻略类 文案风格
  cpyTravelGuide: 'd2b77f8f-1f8c-4f8c-9224-4c5e8f2b0201',
  cpyTravelList: 'd2b77f8f-1f8c-4f8c-9224-4c5e8f2b0202',
  cpyTravelQA: 'd2b77f8f-1f8c-4f8c-9224-4c5e8f2b0203',
  cpyTravelTimeline: 'd2b77f8f-1f8c-4f8c-9224-4c5e8f2b0204',
}

interface NoteTemplateSeed {
  template: NoteTemplate
  slots: NoteImageSlot[]
}

export interface NoteStyleSeed {
  style: NoteStyle
  copywritingStyles: CopywritingStyle[]  // 文案风格列表
  templates: NoteTemplateSeed[]
}

// 旧下拉选项映射到新风格/模板，便于迁移
export const LEGACY_STYLE_MAPPING: Record<ReceiptType, { styleId: string; templateId: string }> = {
  ugc: { styleId: IDS.styleFoodUcg, templateId: IDS.tplFoodUcg01 },
  expert: { styleId: IDS.styleTravelGuide, templateId: IDS.tplTravelGuide01 },
}

const now = Date.now()

export const defaultNoteStyles: NoteStyleSeed[] = [
  {
    style: {
      id: IDS.styleFoodUcg,
      name: '餐饮探店UCG',
      description: '线下探店，突出真实体验与人气感',
      reference_images: [
        'reference/image/ucg/全桌图1.png',
        'reference/image/ucg/菜品图1.jpg',
      ],
      created_at: now,
    },
    copywritingStyles: [
      {
        id: IDS.cpyFoodBestie,
        style_id: IDS.styleFoodUcg,
        name: '闺蜜分享',
        tone: '亲切、口语化、带语气词',
        char_min: 80,
        char_max: 150,
        hashtag_count: 5,
        created_at: now,
      },
      {
        id: IDS.cpyFoodDiary,
        style_id: IDS.styleFoodUcg,
        name: '个人日记',
        tone: '真诚、细腻、有故事感',
        char_min: 80,
        char_max: 150,
        hashtag_count: 5,
        created_at: now,
      },
      {
        id: IDS.cpyFoodReview,
        style_id: IDS.styleFoodUcg,
        name: '真实测评',
        tone: '客观、有细节、讲体验',
        char_min: 80,
        char_max: 150,
        hashtag_count: 5,
        created_at: now,
      },
      {
        id: IDS.cpyFoodExcited,
        style_id: IDS.styleFoodUcg,
        name: '激动安利',
        tone: '热情、夸张、感染力强',
        char_min: 80,
        char_max: 150,
        hashtag_count: 5,
        created_at: now,
      },
    ],
    templates: [
      {
        template: {
          id: IDS.tplFoodUcg01,
          style_id: IDS.styleFoodUcg,
          name: '风格1-探店笔记',
          description: '真实探店记录，必含门头与人气氛围，菜品细节可选',
          image_count_min: 5,
          image_count_max: 8,
          ai_image_max: 3,
          reference_images: [
            'reference/image/ucg/全桌图1.png',
            'reference/image/ucg/菜品图1.jpg',
          ],
          created_at: now,
        },
        slots: [
          {
            id: IDS.slotFoodStorefront,
            template_id: IDS.tplFoodUcg01,
            name: '门头图',
            code: 'storefront',
            title: '门头图',
            description: '门头+招牌清晰，最好有人物路过增加生活感',
            required: true,
            min_count: 1,
            max_count: 2,
            composition: '竖幅或仰拍，店招置于画面上半部分，保留留白',
            reference_images: ['reference/image/ucg/全桌图1.png'],
            created_at: now,
          },
          {
            id: IDS.slotFoodCrowd,
            template_id: IDS.tplFoodUcg01,
            name: '人气图',
            code: 'crowd',
            title: '人气图',
            description: '排队/满座场景，突出火爆程度',
            required: true,
            min_count: 1,
            max_count: 3,
            composition: '横幅或中景，取景包含排队人群与门口/堂食区',
            reference_images: [],
            created_at: now,
          },
          {
            id: IDS.slotFoodTable,
            template_id: IDS.tplFoodUcg01,
            name: '全桌图',
            code: 'full_table',
            title: '全桌图',
            description: '桌面铺满菜品，适当摆放筷子/水杯增加烟火气',
            required: false,
            min_count: 1,
            max_count: 3,
            composition: '俯拍或45°俯拍，保持桌面边缘平衡；适合拼图',
            reference_images: ['reference/image/ucg/全桌图1.png'],
            created_at: now,
          },
          {
            id: IDS.slotFoodDish,
            template_id: IDS.tplFoodUcg01,
            name: '菜品特写',
            code: 'dish_closeup',
            title: '菜品特写',
            description: '热门菜/招牌菜近景，展示质感与卖点',
            required: false,
            min_count: 1,
            max_count: 4,
            composition: '浅景深特写，突出主菜，背景虚化但保留环境氛围',
            reference_images: ['reference/image/ucg/菜品图1.jpg'],
            created_at: now,
          },
        ],
      },
    ],
  },
  {
    style: {
      id: IDS.styleTravelGuide,
      name: '旅游攻略类',
      description: '信息密度高、含文字排版的攻略型笔记',
      reference_images: [
        'reference/image/travel_plan/02_spots_list.png',
        'reference/image/travel_plan/04_tips_handwritten.png',
      ],
      created_at: now,
    },
    copywritingStyles: [
      {
        id: IDS.cpyTravelGuide,
        style_id: IDS.styleTravelGuide,
        name: '详细攻略',
        tone: '专业、有条理、信息全面',
        char_min: 150,
        char_max: 300,
        hashtag_count: 8,
        created_at: now,
      },
      {
        id: IDS.cpyTravelList,
        style_id: IDS.styleTravelGuide,
        name: '清单总结',
        tone: '简洁、有重点、易收藏',
        char_min: 150,
        char_max: 300,
        hashtag_count: 8,
        created_at: now,
      },
      {
        id: IDS.cpyTravelQA,
        style_id: IDS.styleTravelGuide,
        name: '问答科普',
        tone: '亲和、解答疑惑、有互动感',
        char_min: 150,
        char_max: 300,
        hashtag_count: 8,
        created_at: now,
      },
      {
        id: IDS.cpyTravelTimeline,
        style_id: IDS.styleTravelGuide,
        name: '时间线',
        tone: '有节奏、有场景感',
        char_min: 150,
        char_max: 300,
        hashtag_count: 8,
        created_at: now,
      },
    ],
    templates: [
      {
        template: {
          id: IDS.tplTravelGuide01,
          style_id: IDS.styleTravelGuide,
          name: '风格1-旅游攻略笔记',
          description: '含文字排版/清单卡片，覆盖行程、亮点、交通与贴士',
          image_count_min: 7,
          image_count_max: 9,
          ai_image_max: 6,
          reference_images: [
            'reference/image/travel_plan/02_spots_list.png',
            'reference/image/travel_plan/04_tips_handwritten.png',
          ],
          created_at: now,
        },
        slots: [
          {
            id: IDS.slotTravelCover,
            template_id: IDS.tplTravelGuide01,
            name: '封面标题图',
            code: 'cover',
            title: '封面标题图',
            description: '目的地+主题标题，强调风格/季节',
            required: true,
            min_count: 1,
            max_count: 1,
            composition: '大标题+副标题，底图可为地标或纹理底色',
            reference_images: ['reference/image/travel_plan/02_spots_list.png'],
            created_at: now,
          },
          {
            id: IDS.slotTravelItinerary,
            template_id: IDS.tplTravelGuide01,
            name: '行程概览卡片',
            code: 'itinerary_overview',
            title: '行程概览卡片',
            description: '按天或半天的行程/时间段快速卡片',
            required: true,
            min_count: 2,
            max_count: 2,
            composition: '分栏/时间轴，突出地名+时间+亮点关键词',
            reference_images: ['reference/image/travel_plan/02_spots_list.png'],
            created_at: now,
          },
          {
            id: IDS.slotTravelSpots,
            template_id: IDS.tplTravelGuide01,
            name: '景点亮点清单',
            code: 'spot_highlights',
            title: '景点亮点清单',
            description: '核心景点或体验列表，含看点/玩法/时长',
            required: true,
            min_count: 2,
            max_count: 2,
            composition: '清单式或分块排版，可穿插小图标/序号',
            reference_images: ['reference/image/travel_plan/02_spots_list.png'],
            created_at: now,
          },
          {
            id: IDS.slotTravelTraffic,
            template_id: IDS.tplTravelGuide01,
            name: '交通/时间/费用提示',
            code: 'traffic_cost',
            title: '交通/时间/费用提示',
            description: '交通方式、车次时间、预算提示，适度表格化',
            required: true,
            min_count: 1,
            max_count: 1,
            composition: '表格/双色块突出关键信息，留有备注区',
            reference_images: [],
            created_at: now,
          },
          {
            id: IDS.slotTravelFood,
            template_id: IDS.tplTravelGuide01,
            name: '必吃美食合集',
            code: 'food_picks',
            title: '必吃美食合集',
            description: '当地招牌/网红美食，小图拼接即可',
            required: false,
            min_count: 0,
            max_count: 1,
            composition: '多图拼贴或两栏排版，标注店名/招牌菜',
            reference_images: ['reference/image/ucg/菜品图1.jpg'],
            created_at: now,
          },
          {
            id: IDS.slotTravelRoute,
            template_id: IDS.tplTravelGuide01,
            name: '路线/动线示意',
            code: 'route_map',
            title: '路线/动线示意',
            description: '景点连线或步行/交通动线',
            required: false,
            min_count: 0,
            max_count: 1,
            composition: '简单地图或节点连线示意，突出起点/终点',
            reference_images: [],
            created_at: now,
          },
          {
            id: IDS.slotTravelTips,
            template_id: IDS.tplTravelGuide01,
            name: '手写Tips/注意事项',
            code: 'handwritten_tips',
            title: '手写Tips/注意事项',
            description: '高频问题、避坑提醒、随手小贴士，文字为主',
            required: true,
            min_count: 1,
            max_count: 1,
            composition: '手写或拟手写风，留白+图标强调重点',
            reference_images: ['reference/image/travel_plan/04_tips_handwritten.png'],
            created_at: now,
          },
        ],
      },
    ],
  },
]

